import { useState, useEffect, useRef } from "react";
import { Link } from "react-router";
import { CheckCircle2, Upload, Package, Ruler, Layers, Palette, ChevronDown, Plus, Minus, ArrowRight } from "lucide-react";
import { useInViewOnce } from "../hooks/useInViewOnce";
import { cn } from "../components/ui/utils";
import { HEADLINE_GRADIENT } from "../components/GradientSectionHeading";

function AccordionItem({ question, answer }: { question: string; answer: string }) {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div className="w-full border-b border-[#131312]/10 transition-all duration-300">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full py-5 flex items-start justify-between text-left group"
      >
        <span className="text-xl font-medium text-[#131312] group-hover:opacity-70 transition-opacity pr-8">
          {question}
        </span>
        <div className="flex-shrink-0 text-[#131312]/40 group-hover:text-[#131312] transition-colors mt-1.5">
          {isOpen ? <Minus className="size-5" /> : <Plus className="size-5" />}
        </div>
      </button>
      <div className={cn(
        "overflow-hidden transition-all duration-500 ease-in-out",
        isOpen ? "max-h-[500px] pb-5 opacity-100" : "max-h-0 opacity-0"
      )}>
        <p className="text-[#131312]/60 leading-relaxed text-lg w-full">
          {answer}
        </p>
      </div>
    </div>
  );
}

export function QuotePage() {
  const { ref: headlineRef, inView: headlineInView } = useInViewOnce({ threshold: 0.28, rootMargin: "0px 0px -10% 0px" });
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    company: "",
    phone: "",
    productType: "",
    material: "",
    size: "",
    quantity: "",
    finishes: [] as string[],
    additionalInfo: "",
  });

  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [files, setFiles] = useState<File[]>([]);
  const [fileError, setFileError] = useState("");
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [productTypeOpen, setProductTypeOpen] = useState(false);
  const [materialOpen, setMaterialOpen] = useState(false);
  const productTypeRef = useRef<HTMLDivElement>(null);
  const materialRef = useRef<HTMLDivElement>(null);

  function handleFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    const selected = Array.from(e.target.files || []);
    const overLimit = selected.filter(f => f.size > 10 * 1024 * 1024);
    if (overLimit.length > 0) {
      setFileError(`File "${overLimit[0].name}" exceeds the 10MB limit.`);
      return;
    }
    setFileError("");
    setFiles(prev => {
      const existing = prev.map(f => f.name);
      return [...prev, ...selected.filter(f => !existing.includes(f.name))];
    });
    e.target.value = "";
  }

  function handleDrop(e: React.DragEvent<HTMLDivElement>) {
    e.preventDefault();
    const dropped = Array.from(e.dataTransfer.files);
    const overLimit = dropped.filter(f => f.size > 10 * 1024 * 1024);
    if (overLimit.length > 0) { setFileError(`File "${overLimit[0].name}" exceeds the 10MB limit.`); return; }
    setFileError("");
    setFiles(prev => { const ex = prev.map(f => f.name); return [...prev, ...dropped.filter(f => !ex.includes(f.name))]; });
  }

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (productTypeRef.current && !productTypeRef.current.contains(e.target as Node)) setProductTypeOpen(false);
      if (materialRef.current && !materialRef.current.contains(e.target as Node)) setMaterialOpen(false);
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const attachments = await Promise.all(
        files.map(file => new Promise<{ filename: string; content: string; contentType: string }>((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => resolve({ filename: file.name, content: (reader.result as string).split(",")[1], contentType: file.type });
          reader.onerror = reject;
          reader.readAsDataURL(file);
        }))
      );
      const res = await fetch("http://localhost:3001/api/send-quote", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...formData, attachments }),
      });
      if (!res.ok) throw new Error("Server error");
      setSubmitted(true);
    } catch {
      setError("Failed to send quote request. Please try again or contact us directly.");
    } finally {
      setLoading(false);
    }
  };


  return (
    <div className="min-h-screen bg-[#FBFAF7]">
      <section className="py-20">
        <div className="mx-auto max-w-7xl px-6 lg:px-8 text-center">
          <h1 ref={headlineRef} className="text-[48px] sm:text-[60px] md:text-[70px] lg:text-[78.1px] font-semibold mb-8 leading-tight text-[#1C1C1C]">
            <span
              className={cn("gradient-text-animate inline", headlineInView && "gradient-text-animate--play")}
              style={{ backgroundImage: HEADLINE_GRADIENT }}
            >
              Get Instant Quote 
            </span>
          </h1>
          <p
            className="font-bold leading-[25px] text-[#1C1C1C] mx-auto"
            style={{
              fontFamily: "'Inter', sans-serif",
              fontSize: '18.6px',
              maxWidth: '627px',
              letterSpacing: '-0.5px'
            }}
          >
            Tell us about your packaging needs and we'll provide a detailed quote within 24 hours
          </p>
        </div>
      </section>

      <section className="pb-24">
        <div className="mx-auto max-w-4xl px-6 lg:px-8">
          <div className="bg-[#F7F4ED] rounded-3xl shadow-xl border border-[#131312]/5 p-8 md:p-12">
            <form onSubmit={handleSubmit} className="space-y-8">
              <div>
                <h2 className="text-2xl font-semibold text-[#131312] mb-6">Contact Information</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-bold text-[#131312] mb-2 font-['Inter']">Full Name *</label>
                    <input
                      type="text"
                      required
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      className="w-full px-4 py-3 bg-[#FBFAF7] border border-[#131312]/10 rounded-xl focus:outline-none focus:ring-1 focus:ring-[#131312]/20"
                      placeholder="John Smith"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-bold text-[#131312] mb-2 font-['Inter']">Email Address *</label>
                    <input
                      type="email"
                      required
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      className="w-full px-4 py-3 bg-[#FBFAF7] border border-[#131312]/10 rounded-xl focus:outline-none focus:ring-1 focus:ring-[#131312]/20"
                      placeholder="john@company.com"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-bold text-[#131312] mb-2 font-['Inter']">Company Name</label>
                    <input
                      type="text"
                      value={formData.company}
                      onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                      className="w-full px-4 py-3 bg-[#FBFAF7] border border-[#131312]/10 rounded-xl focus:outline-none focus:ring-1 focus:ring-[#131312]/20"
                      placeholder="Your Company Ltd"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-bold text-[#131312] mb-2 font-['Inter']">Phone Number</label>
                    <input
                      type="tel"
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      className="w-full px-4 py-3 bg-[#FBFAF7] border border-[#131312]/10 rounded-xl focus:outline-none focus:ring-1 focus:ring-[#131312]/20"
                      placeholder="+92 300 8666239"
                    />
                  </div>
                </div>
              </div>

              <div className="border-t border-[#131312]/5 pt-8">
                <h2 className="text-2xl font-semibold text-[#131312] mb-6">Product Details</h2>
                <div className="space-y-6">
                  <div>
                    <label className="block text-sm font-bold text-[#131312] mb-2 font-['Inter']">Product Type *</label>
                    <div className="relative" ref={productTypeRef}>
                      <button
                        type="button"
                        onClick={() => { setProductTypeOpen(!productTypeOpen); setMaterialOpen(false); }}
                        className="w-full px-4 py-3 bg-[#FBFAF7] border border-[#131312]/10 rounded-xl text-left focus:outline-none focus:ring-1 focus:ring-[#131312]/20"
                      >
                        <span className={formData.productType ? 'text-[#131312]' : 'text-[#131312]/40'}>
                          {formData.productType ? [
                            { value: 'textile-home', label: 'Textile & Home Packaging' }, { value: 'garment', label: 'Garment Packaging' },
                            { value: 'luxury', label: 'Luxury Packaging' }, { value: 'labels-stickers', label: 'Labels & Stickers' },
                            { value: 'custom', label: 'Custom Packaging' }, { value: 'retail-display', label: 'Retail & Display Packaging' },
                            { value: 'eco-friendly', label: 'Eco-Friendly Packaging' }, { value: 'business-print', label: 'Business Print Materials' },
                          ].find(o => o.value === formData.productType)?.label : 'Select product type'}
                        </span>
                        <ChevronDown className={cn('absolute right-4 top-1/2 -translate-y-1/2 size-5 text-[#131312]/40 transition-transform pointer-events-none', productTypeOpen && 'rotate-180')} />
                      </button>
                      {productTypeOpen && (
                        <div className="absolute z-20 mt-1 w-full bg-[#FBFAF7] border border-[#131312]/10 rounded-xl shadow-lg overflow-hidden">
                          {[
                            { value: 'textile-home', label: 'Textile & Home Packaging' }, { value: 'garment', label: 'Garment Packaging' },
                            { value: 'luxury', label: 'Luxury Packaging' }, { value: 'labels-stickers', label: 'Labels & Stickers' },
                            { value: 'custom', label: 'Custom Packaging' }, { value: 'retail-display', label: 'Retail & Display Packaging' },
                            { value: 'eco-friendly', label: 'Eco-Friendly Packaging' }, { value: 'business-print', label: 'Business Print Materials' },
                          ].map((opt) => (
                            <button
                              key={opt.value}
                              type="button"
                              onClick={() => { setFormData({ ...formData, productType: opt.value }); setProductTypeOpen(false); }}
                              className={cn('w-full px-4 py-2.5 text-left text-sm hover:bg-[#F0EDE6] transition-colors', formData.productType === opt.value ? 'font-semibold text-[#131312] bg-[#EDE9E1]' : 'text-[#131312]/70')}
                            >
                              {opt.label}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-bold text-[#131312] mb-2 font-['Inter']">Material Preference *</label>
                    <div className="relative" ref={materialRef}>
                      <button
                        type="button"
                        onClick={() => { setMaterialOpen(!materialOpen); setProductTypeOpen(false); }}
                        className="w-full px-4 py-3 bg-[#FBFAF7] border border-[#131312]/10 rounded-xl text-left focus:outline-none focus:ring-1 focus:ring-[#131312]/20"
                      >
                        <span className={formData.material ? 'text-[#131312]' : 'text-[#131312]/40'}>
                          {formData.material ? [
                            { value: 'sbs-c1s', label: 'SBS C1S' }, { value: 'sbs-c2s', label: 'SBS C2S' },
                            { value: 'kraft', label: 'Kraft Board' }, { value: 'duplex', label: 'Duplex Board' },
                            { value: 'corrugated', label: 'Corrugated' }, { value: 'chipboard', label: 'Chipboard (Rigid)' },
                            { value: 'recycled', label: 'Recycled/Eco' }, { value: 'not-sure', label: 'Not Sure' },
                          ].find(o => o.value === formData.material)?.label : 'Select material'}
                        </span>
                        <ChevronDown className={cn('absolute right-4 top-1/2 -translate-y-1/2 size-5 text-[#131312]/40 transition-transform pointer-events-none', materialOpen && 'rotate-180')} />
                      </button>
                      {materialOpen && (
                        <div className="absolute z-20 mt-1 w-full bg-[#FBFAF7] border border-[#131312]/10 rounded-xl shadow-lg overflow-hidden">
                          {[
                            { value: 'sbs-c1s', label: 'SBS C1S' }, { value: 'sbs-c2s', label: 'SBS C2S' },
                            { value: 'kraft', label: 'Kraft Board' }, { value: 'duplex', label: 'Duplex Board' },
                            { value: 'corrugated', label: 'Corrugated' }, { value: 'chipboard', label: 'Chipboard (Rigid)' },
                            { value: 'recycled', label: 'Recycled/Eco' }, { value: 'not-sure', label: 'Not Sure' },
                          ].map((opt) => (
                            <button
                              key={opt.value}
                              type="button"
                              onClick={() => { setFormData({ ...formData, material: opt.value }); setMaterialOpen(false); }}
                              className={cn('w-full px-4 py-2.5 text-left text-sm hover:bg-[#F0EDE6] transition-colors', formData.material === opt.value ? 'font-semibold text-[#131312] bg-[#EDE9E1]' : 'text-[#131312]/70')}
                            >
                              {opt.label}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-bold text-[#131312] mb-2 font-['Inter']">Size/Dimensions *</label>
                      <div className="relative">
                        <Ruler className="absolute left-3 top-1/2 -translate-y-1/2 size-5 text-[#131312]/40" />
                        <input
                          type="text"
                          required
                          value={formData.size}
                          onChange={(e) => setFormData({ ...formData, size: e.target.value })}
                          className="w-full pl-12 pr-4 py-3 bg-[#FBFAF7] border border-[#131312]/10 rounded-xl focus:outline-none focus:ring-1 focus:ring-[#131312]/20"
                          placeholder="e.g., 90mm x 60mm"
                        />
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-bold text-[#131312] mb-2 font-['Inter']">Quantity *</label>
                      <div className="relative">
                        <Package className="absolute left-3 top-1/2 -translate-y-1/2 size-5 text-[#131312]/40" />
                        <input
                          type="text"
                          required
                          value={formData.quantity}
                          onChange={(e) => setFormData({ ...formData, quantity: e.target.value })}
                          className="w-full pl-12 pr-4 py-3 bg-[#FBFAF7] border border-[#131312]/10 rounded-xl focus:outline-none focus:ring-1 focus:ring-[#131312]/20"
                          placeholder="e.g., 5000 units"
                        />
                      </div>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-bold text-[#131312] mb-3 font-['Inter']">Finishing Options</label>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                      {[
                        { value: "matte", label: "Matte Coating" },
                        { value: "gloss", label: "Gloss Coating" },
                        { value: "soft-touch", label: "Soft Touch" },
                        { value: "foil", label: "Foil Stamping" },
                        { value: "emboss", label: "Emboss/Deboss" },
                        { value: "spot-uv", label: "Spot UV" },
                      ].map((finish) => {
                        const checked = formData.finishes.includes(finish.value);
                        return (
                          <button
                            key={finish.value}
                            type="button"
                            onClick={() => {
                              if (checked) {
                                setFormData({ ...formData, finishes: formData.finishes.filter((f) => f !== finish.value) });
                              } else {
                                setFormData({ ...formData, finishes: [...formData.finishes, finish.value] });
                              }
                            }}
                            className={cn(
                              'flex items-center gap-2.5 p-3 rounded-xl border cursor-pointer transition-all text-left',
                              checked
                                ? 'bg-[#131312] border-[#131312] text-white'
                                : 'bg-[#FBFAF7] border-[#131312]/10 text-[#131312] hover:border-[#131312]/30'
                            )}
                          >
                            <span className={cn('size-4 rounded flex items-center justify-center shrink-0 border transition-all', checked ? 'bg-white border-white' : 'border-[#131312]/20')}>
                              {checked && <svg className="size-2.5" viewBox="0 0 10 8" fill="none"><path d="M1 4l3 3 5-6" stroke="#131312" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>}
                            </span>
                            <span className="text-sm font-medium">{finish.label}</span>
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-bold text-[#131312] mb-2 font-['Inter']">Additional Information</label>
                    <textarea
                      value={formData.additionalInfo}
                      onChange={(e) => setFormData({ ...formData, additionalInfo: e.target.value })}
                      rows={4}
                      className="w-full px-4 py-3 bg-[#FBFAF7] border border-[#131312]/10 rounded-xl focus:outline-none focus:ring-1 focus:ring-[#131312]/20 resize-none"
                      placeholder="Any additional details about your project, timeline, special requirements, etc."
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-bold text-[#131312] mb-3 font-['Inter']">Upload Files (Optional)</label>
                    <div
                      className="border-2 border-dashed border-[#131312]/10 rounded-2xl p-8 text-center hover:border-[#131312]/30 transition-colors cursor-pointer bg-white/30"
                      onClick={() => fileInputRef.current?.click()}
                      onDragOver={e => e.preventDefault()}
                      onDrop={handleDrop}
                    >
                      <Upload className="size-10 text-[#131312]/40 mx-auto mb-4" />
                      <p className="text-sm font-medium text-[#131312] mb-1">Drag and drop files here or <span className="underline">browse</span></p>
                      <p className="text-xs text-[#131312]/50">Artwork, design files, or reference images — Max 10MB each</p>
                      <input ref={fileInputRef} type="file" multiple className="hidden" onChange={handleFileChange} accept="image/*,.pdf,.ai,.eps,.psd,.zip" />
                    </div>
                    {fileError && <p className="text-red-600 text-xs mt-2">{fileError}</p>}
                    {files.length > 0 && (
                      <ul className="mt-3 space-y-2">
                        {files.map((f, i) => (
                          <li key={i} className="flex items-center justify-between px-3 py-2 bg-white border border-[#131312]/10 rounded-lg text-sm">
                            <span className="text-[#131312] truncate max-w-[80%]">{f.name}</span>
                            <button type="button" onClick={() => setFiles(files.filter((_, j) => j !== i))} className="text-[#131312]/40 hover:text-[#131312] ml-2 text-xs">✕</button>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                </div>
              </div>

              <div className="border-t border-[#131312]/5 pt-8">
                {error && (
                  <p className="text-red-600 text-sm bg-red-50 border border-red-200 rounded-lg px-4 py-3 mb-4">{error}</p>
                )}
                <div className="mb-6 p-4 bg-[#F6F2EB] rounded-xl border border-[#131312]/10 text-sm text-[#131312]/80">
                  <strong>Note:</strong> Formal quotations, invoices and banking details will be issued under our legal entity, Paper Printing Services.
                </div>
                <div className="flex flex-col sm:flex-row gap-4">
                  <button
                    type="submit"
                    disabled={loading || submitted}
                    className="flex-1 inline-flex items-center justify-center px-8 py-4 bg-[#131312] text-white transition-all hover:bg-[#131312]/90 rounded-xl font-medium shadow-lg shadow-[#131312]/10 h-14 disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    {loading ? "Sending..." : "Submit Quote Request"}
                  </button>
                </div>
                {submitted && (
                  <div className="flex flex-col items-center gap-2 py-6 text-center">
                    <div className="inline-flex items-center justify-center size-12 rounded-full bg-[#131312] text-white mb-1">
                      <CheckCircle2 className="size-5" />
                    </div>
                    <p className="text-base font-semibold text-[#131312]">Quote Request Received!</p>
                    <p className="text-sm text-muted-foreground">We'll review your requirements and get back to you within 24 hours.</p>
                  </div>
                )}
                <p className="text-xs text-[#131312]/40 mt-6 text-center">
                  By submitting this form, you agree to our Terms of Service and Privacy Policy
                </p>
              </div>
            </form>
          </div>

          <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="bg-[#F7F4ED] rounded-xl p-6 text-center border border-[#131312]/10">
              <div className="icon-box size-14 mb-4 bg-[#FBFAF7] rounded-2xl flex items-center justify-center mx-auto">
                <Package className="size-7 text-[#131312]" />
              </div>
              <h3 className="font-semibold text-[#131312] mb-2">Fast Response</h3>
              <p className="text-sm text-[#131312]/70">Quote within 24 hours</p>
            </div>
            <div className="bg-[#F7F4ED] rounded-xl p-6 text-center border border-[#131312]/10">
              <div className="icon-box size-14 mb-4 bg-[#FBFAF7] rounded-2xl flex items-center justify-center mx-auto">
                <Layers className="size-7 text-[#131312]" />
              </div>
              <h3 className="font-semibold text-[#131312] mb-2">Free Samples</h3>
              <p className="text-sm text-[#131312]/70">Physical samples available</p>
            </div>
            <div className="bg-[#F7F4ED] rounded-xl p-6 text-center border border-[#131312]/10">
              <div className="icon-box size-14 mb-4 bg-[#FBFAF7] rounded-2xl flex items-center justify-center mx-auto">
                <Palette className="size-7 text-[#131312]" />
              </div>
              <h3 className="font-semibold text-[#131312] mb-2">Expert Guidance</h3>
              <p className="text-sm text-[#131312]/70">Consultation included</p>
            </div>
          </div>
        </div>
      </section>

      <section className="py-32 bg-[#F7F4ED]">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-24 items-start">
            <div>
              <h2 className="text-4xl md:text-4xl font-semibold leading-[1.1] tracking-tight text-[#131312] leading-tight mb-8">
                Frequently Asked <br className="hidden lg:block" /> Questions
              </h2>
              <p className="text-[#131312]/70 text-lg max-w-sm">
                Can't find what you're looking for? Reach out to our team for personalized assistance.
              </p>
            </div>

            <div className="flex flex-col items-start p-0 w-full lg:max-w-[586px] flex-grow self-stretch space-y-0">
              <AccordionItem
                question="What's your minimum order quantity?"
                answer="MOQs vary by product type. Contact us for specific requirements for your project."
              />
              <AccordionItem
                question="Do you ship internationally?"
                answer="Yes, we ship to UK, EU, USA, and other international markets."
              />
              <AccordionItem
                question="How long does production take?"
                answer="Typically 10-15 days after sample approval, depending on order complexity."
              />
              <AccordionItem
                question="Can I get physical samples?"
                answer="Yes, we provide physical samples before production at no additional cost."
              />
            </div>
          </div>
        </div>
      </section>

      <section className="relative py-24 bg-[#131312] text-white overflow-hidden">
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            background: `
            radial-gradient(72.35% 197.74% at 70% 50%, rgba(255, 255, 255, 0.07) 0%, rgba(0, 0, 0, 0) 50%),
            radial-gradient(72.35% 197.74% at 30% 50%, rgba(255, 255, 255, 0.07) 0%, rgba(0, 0, 0, 0) 50%)
          `
          }}
        />
        <div className="relative z-10 mx-auto max-w-7xl px-6 lg:px-8 text-center">
          <h2 className="text-4xl md:text-6xl font-bold mb-6 text-white max-w-4xl mx-auto leading-[1.1]">
            Ready to Create Premium <br /> Packaging?
          </h2>
          <p className="text-xl text-white/80 mb-12 max-w-2xl mx-auto">
            Get an instant quote and see how we can elevate your brand with custom packaging solutions
          </p>
          <div className="flex flex-col items-center justify-center gap-4 sm:flex-row">
            <Link
              to="/quote"
              className="inline-flex h-14 w-full max-w-[280px] items-center justify-center rounded-xl bg-white text-base font-medium text-[#131312] transition-colors hover:bg-white/90 shadow-lg sm:w-[223px]"
            >
              Get Instant Quote
              <ArrowRight className="ml-2 size-5" />
            </Link>
            <Link
              to="/contact"
              className="inline-flex h-14 w-full max-w-[280px] items-center justify-center rounded-xl border border-white/20 bg-white/10 px-8 text-base font-medium text-white backdrop-blur-sm transition-colors hover:bg-white/20 sm:w-auto"
            >
              Schedule Consultation
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
